import { useEffect } from 'react';

const SITE_URL = 'https://boundr.in';
const SITE_NAME = 'Boundr';

export interface SeoProps {
  /** Document title. Rendered as-is (include the brand suffix if you want one). */
  title: string;
  /** Meta description (~150-160 chars). */
  description: string;
  /** Route path, e.g. '/about'. Used to build the absolute canonical + og:url. */
  path: string;
  /** Override the canonical URL entirely (absolute). Defaults to SITE_URL + path. */
  canonical?: string;
  /** Absolute or root-relative URL to a 1200x630 OG/Twitter card image. */
  image?: string;
  /** Image dimensions for og:image:width/height (requires image to be set). */
  imageWidth?: number;
  imageHeight?: number;
  /** hreflang alternate links: array of { hreflang, href } pairs. */
  hreflang?: { hreflang: string; href: string }[];
}

/**
 * Sets per-page document head (title, description, canonical, Open Graph,
 * Twitter) imperatively. Used by prerendered marketing routes so the static
 * HTML snapshot produced by scripts/prerender.mjs carries the right SEO tags.
 *
 * Implementation note: this runs in useEffect (client + headless-browser
 * capture). It is intentionally NOT SSR-rendered markup — the prerender step
 * captures the resolved DOM, so useEffect mutations are included in the output.
 */
export function Seo({
  title,
  description,
  path,
  canonical,
  image,
  imageWidth,
  imageHeight,
  hreflang,
}: SeoProps) {
  useEffect(() => {
    const url = canonical ?? `${SITE_URL}${path}`;
    const imageUrl = image ? (image.startsWith('http') ? image : `${SITE_URL}${image}`) : undefined;
    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', 'index, follow');
    upsertLink('canonical', url);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'en_IN');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    if (imageUrl) {
      upsertMeta('property', 'og:image', imageUrl);
      upsertMeta('name', 'twitter:image', imageUrl);
      if (imageWidth) upsertMeta('property', 'og:image:width', String(imageWidth));
      if (imageHeight) upsertMeta('property', 'og:image:height', String(imageHeight));
    }

    if (hreflang) {
      removeHreflangLinks();
      for (const { hreflang: lang, href } of hreflang) {
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', lang);
        el.setAttribute('href', href);
        document.head.appendChild(el);
      }
    }
  }, [title, description, path, canonical, image, imageWidth, imageHeight, hreflang]);

  return null;
}

function upsertMeta(keyAttr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${keyAttr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(keyAttr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export interface JsonLdProps {
  /** Arbitrary schema.org structured data object (e.g. FAQPage). */
  data: Record<string, unknown>;
  /** DOM id for the injected <script> tag — pass a stable id per schema type per page. */
  id: string;
}

/**
 * Injects a JSON-LD <script type="application/ld+json"> tag into <head>.
 * Same prerender-capture caveat as <Seo>: runs in useEffect, captured by
 * scripts/prerender.mjs into the static HTML snapshot.
 */
export function JsonLd({ data, id }: JsonLdProps) {
  useEffect(() => {
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
  }, [data, id]);

  return null;
}

function removeHreflangLinks() {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
